import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Search, MessageSquare } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import ScholarAvatar from "@/components/ScholarAvatar";

export default function Discover() {
  const { user, meta, setFollowing } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("All");
  const [scholars, setScholars] = useState([]);
  const [followingIds, setFollowingIds] = useState(new Set(user?.following || []));
  const [pendingIds, setPendingIds] = useState(new Set());
  useEffect(() => { setFollowingIds(new Set(user?.following || [])); }, [user?.following]);

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (specialty !== "All") params.specialty = specialty;
      const { data } = await api.get("/users", { params });
      setScholars(data.filter((u) => u.id !== user?.id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { if (user?.id) load(); /* eslint-disable-next-line */ }, [specialty, user?.id]);

  const toggleFollow = async (id) => {
    if (pendingIds.has(id)) return; // already in flight
    const wasFollowing = followingIds.has(id);
    // Optimistic UI + lock this row
    setPendingIds((s) => { const n = new Set(s); n.add(id); return n; });
    setFollowingIds((s) => {
      const n = new Set(s);
      if (wasFollowing) n.delete(id); else n.add(id);
      return n;
    });
    try {
      if (wasFollowing) await api.delete(`/users/${id}/follow`);
      else { await api.post(`/users/${id}/follow`); toast.success("Now following"); }
      setFollowing(id, !wasFollowing);
    } catch (e) {
      // Revert on failure
      setFollowingIds((s) => {
        const n = new Set(s);
        if (wasFollowing) n.add(id); else n.delete(id);
        return n;
      });
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPendingIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  };

  const startChat = async (peerId) => {
    try {
      const { data } = await api.post("/conversations", { peer_id: peerId });
      navigate(`/app/chat/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <div className="label-eyebrow">Discover</div>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[color:var(--ayur-emerald)] mt-2">Find scholars & practitioners</h1>
      </div>

      <div className="paper-card p-5">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ayur-mute)]" />
            <Input
              data-testid="scholar-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, college, university…"
              className="bg-white pl-9"
            />
          </div>
          <Button type="submit" className="bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white">Search</Button>
        </form>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            data-testid="specialty-filter-all"
            onClick={() => setSpecialty("All")}
            className={`pill ${specialty === "All" ? "!bg-[color:var(--ayur-emerald)] !text-white !border-transparent" : ""}`}
          >All</button>
          {meta.specialties.map((s) => (
            <button
              key={s}
              data-testid={`specialty-filter-${s.toLowerCase().replace(/[^a-z]/g, "")}`}
              onClick={() => setSpecialty(s)}
              className={`pill ${specialty === s ? "!bg-[color:var(--ayur-emerald)] !text-white !border-transparent" : ""}`}
            >{s}</button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {scholars.length === 0 && (
          <div className="col-span-full paper-card p-12 text-center">
            <div className="label-eyebrow">No scholars</div>
            <h3 className="font-serif-display text-2xl mt-2">Try a different filter</h3>
          </div>
        )}
        {scholars.map((s) => (
          <div key={s.id} data-testid="scholar-card" className="paper-card p-5 flex gap-4">
            <ScholarAvatar user={s} size={56} />
            <div className="flex-1 min-w-0">
              <Link to={`/app/scholars/${s.id}`} data-testid="view-scholar-profile-button" className="font-semibold text-[color:var(--ayur-ink)] link-underline inline-flex items-center gap-1">
                {s.full_name}
                {s.verified && <VerifiedBadge size={14} />}
              </Link>
              <div className="text-xs text-[color:var(--ayur-mute)] mt-0.5">
                {s.degree}{s.specialty ? ` · ${s.specialty}` : ""}
              </div>
              <div className="text-xs text-[color:var(--ayur-mute)] mt-1 truncate">
                {s.college}{s.batch_year ? ` · Batch ${s.batch_year}` : ""}
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  data-testid="follow-scholar-button"
                  onClick={() => toggleFollow(s.id)}
                  className={followingIds.has(s.id)
                    ? "bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-badge)]"
                    : "bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"}
                >
                  {followingIds.has(s.id) ? "Following" : "Follow"}
                </Button>
                <Button
                  size="sm" variant="outline"
                  data-testid="message-scholar-button"
                  onClick={() => startChat(s.id)}
                  className="border-[color:var(--ayur-border)] text-[color:var(--ayur-emerald)]"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Message
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
